import axios from 'axios';

// ─── Axios instance ───────────────────────────────────────────
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor — attach Bearer token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('inventapro_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — normalize errors + handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('inventapro_token');
      localStorage.removeItem('inventapro_user');
      window.location.href = '/login';
    }
    const message =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Error de conexión con el servidor';
    error.displayMessage = message;
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────────────
export const authService = {
  login: (usuario, password) => api.post('/auth/login', { usuario, password }),
  me: () => api.get('/auth/me')
};

// ─── Usuarios ─────────────────────────────────────────────────
export const usuariosService = {
  getAll: () => api.get('/usuarios'),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`)
};

// ─── Auditoría ────────────────────────────────────────────────
export const auditoriaService = {
  getAll: (params = {}) => api.get('/auditoria', { params })
};

// ─── Productos ────────────────────────────────────────────────
export const productosService = {
  getAll: (params = {}) => api.get('/productos', { params }),
  getById: (id) => api.get(`/productos/${id}`),
  create: (data) => api.post('/productos', data),
  update: (id, data) => api.put(`/productos/${id}`, data),
  delete: (id) => api.delete(`/productos/${id}`),
  getTendenciaCostos: (id, params = {}) => api.get(`/productos/${id}/tendencia-costos`, { params })
};

// ─── Movimientos ──────────────────────────────────────────────
export const movimientosService = {
  getAll: (params = {}) => api.get('/movimientos', { params }),
  getById: (id) => api.get(`/movimientos/${id}`),
  create: (data) => api.post('/movimientos', data),
  getByProducto: (producto_id) => api.get('/movimientos', { params: { producto_id } })
};

// ─── Categorías ───────────────────────────────────────────────
export const categoriasService = {
  getAll: () => api.get('/categorias'),
  getById: (id) => api.get(`/categorias/${id}`),
  create: (data) => api.post('/categorias', data),
  update: (id, data) => api.put(`/categorias/${id}`, data),
  delete: (id) => api.delete(`/categorias/${id}`)
};

// ─── Proveedores ──────────────────────────────────────────────
export const proveedoresService = {
  getAll: (params = {}) => api.get('/proveedores', { params }),
  getById: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data) => api.put(`/proveedores/${id}`, data),
  delete: (id) => api.delete(`/proveedores/${id}`)
};

// ─── Órdenes de Compra ────────────────────────────────────────
export const ordenesCompraService = {
  getAll: (params = {}) => api.get('/ordenes-compra', { params }),
  getById: (id) => api.get(`/ordenes-compra/${id}`),
  getSiguienteNumero: (params = {}) => api.get('/ordenes-compra/siguiente-numero', { params }),
  create: (data) => api.post('/ordenes-compra', data),
  update: (id, data) => api.put(`/ordenes-compra/${id}`, data),
  autorizar: (id) => api.put(`/ordenes-compra/${id}/autorizar`),
  rechazar: (id, motivo_rechazo) => api.put(`/ordenes-compra/${id}/rechazar`, { motivo_rechazo }),
  entregar: (id, data) => api.put(`/ordenes-compra/${id}/entregar`, data),
  subirEvidencia: (id, formData) => api.post(`/ordenes-compra/${id}/evidencia`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  subirCotizacion: (id, formData) => api.post(`/ordenes-compra/${id}/cotizaciones`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  eliminarCotizacion: (id, path) => api.delete(`/ordenes-compra/${id}/cotizaciones`, { data: { path } }),
  resolverIncidencia: (id, detalleId, data) => api.put(`/ordenes-compra/${id}/detalles/${detalleId}/resolver-incidencia`, data),
  registrarPago: (id, data) => api.post(`/ordenes-compra/${id}/pagos`, data),
  subirComprobante: (id, formData) => api.post(`/ordenes-compra/${id}/comprobante`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  eliminarPago: (id, pagoId) => api.delete(`/ordenes-compra/${id}/pagos/${pagoId}`),
  delete: (id) => api.delete(`/ordenes-compra/${id}`)
};

// ─── Facturas ─────────────────────────────────────────────────
export const facturasService = {
  getAll: (params = {}) => api.get('/facturas', { params }),
  getById: (id) => api.get(`/facturas/${id}`),
  create: (data) => api.post('/facturas', data),
  update: (id, data) => api.put(`/facturas/${id}`, data),
  revisar: (id, correcciones) => api.put(`/facturas/${id}/revisar`, { correcciones }),
  confirmar: (id, decisiones) => api.put(`/facturas/${id}/confirmar`, { decisiones }),
  delete: (id) => api.delete(`/facturas/${id}`)
};

// ─── Dashboard ────────────────────────────────────────────────
export const dashboardService = {
  getKPIs: () => api.get('/dashboard'),
  getKPIsCategorias: () => api.get('/dashboard/categorias')
};

export default api;
